import { cn } from "../../lib/cn.js";
import {
  CONTRACT_STATUS_LABELS,
  normalizeContractStatus,
  type ContractStatusValue,
} from "./contract-status.js";

export interface StatusBadgeProps {
  status: string;
  size?: "sm" | "md";
  hint?: string;
  upstream?: string;
  pinned?: boolean;
  className?: string;
}

function StatusIcon({ status }: { status: ContractStatusValue }) {
  const cls = "shrink-0 w-3.5 h-3.5";
  switch (status) {
    case "ACTIVE":
      return (
        <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case "REJECTED":
      return (
        <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case "NIGO":
      return (
        <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        </svg>
      );
    default:
      return (
        <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
  }
}

function statusClasses(status: ContractStatusValue) {
  return cn(
    "inline-flex items-center gap-1.5 font-semibold border",
    status === "PENDING" && "bg-status-pending-bg text-status-pending-text border-status-pending-border",
    status === "ACTIVE" && "bg-status-active-bg text-status-active-text border-status-active-border",
    status === "NIGO" && "bg-status-nigo-bg text-status-nigo-text border-status-nigo-border",
    status === "REJECTED" && "bg-status-rejected-bg text-status-rejected-text border-status-rejected-border"
  );
}

export function StatusBadge({ status, size = "md", hint, upstream, pinned, className }: StatusBadgeProps) {
  const normalized = normalizeContractStatus(status);
  const sizeClass = size === "sm" ? "px-2 py-0.5 text-[11px] rounded-md" : "px-3 py-1.5 text-xs rounded-lg";

  return (
    <div className={cn("inline-flex flex-col gap-1", className)}>
      <span className={cn(statusClasses(normalized), sizeClass)}>
        <StatusIcon status={normalized} />
        {CONTRACT_STATUS_LABELS[normalized]}
        {pinned && (
          <svg className="w-3 h-3 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-label="Pinned">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        )}
      </span>
      {hint && <span className="text-xs text-content-secondary leading-snug max-w-xs">{hint}</span>}
      {upstream && !hint && (
        <span className="text-[11px] text-content-muted leading-snug max-w-xs">{upstream}</span>
      )}
    </div>
  );
}

export function ContractStatusStack({
  stats,
  className,
}: {
  stats: { active: number; pending: number; nigo: number; rejected: number };
  className?: string;
}) {
  const allItems: Array<{ status: ContractStatusValue; count: number }> = [
    { status: "ACTIVE", count: stats.active },
    { status: "PENDING", count: stats.pending },
    { status: "NIGO", count: stats.nigo },
    { status: "REJECTED", count: stats.rejected },
  ];
  const items = allItems.filter((item) => item.count > 0);

  if (items.length === 0) {
    return <span className={cn("text-xs text-content-muted", className)}>No contracts</span>;
  }

  return (
    <div className={cn("flex flex-wrap gap-1", className)}>
      {items.map(({ status, count }) => (
        <span key={status} className="inline-flex items-center gap-1">
          <StatusBadge status={status} size="sm" />
          <span className="text-xs tabular-nums text-content-secondary" aria-label={`${count} contracts`}>{count}</span>
        </span>
      ))}
    </div>
  );
}
